import { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Link } from "@mui/material";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormHelperText from "@mui/material/FormHelperText";
import ChevronLeft from "mdi-material-ui/ChevronLeft";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "src/hooks/useAuth";
import ModalMessage from "./ModalMessage";
import { pathForAuthRoute } from "src/@core/utils/urlQueryPreservation";

const schema = yup.object().shape({
  email: yup.string().email().required(),
});

const RequestOTP = ({ otp, setOtp, onSuccess }) => {
  const location = useLocation();
  const auth = useAuth();
  const [messageModal, setMessageModal] = useState({ visible: false, title: "", description: "" });

  const {
    control,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      email: otp.email || "",
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    const { email } = data;

    if (!email) {
      setError("email", { type: "manual", message: "Endereço de e-mail é um campo obrigatório" });
      return;
    }

    setOtp({ otp: "", email: email.toLowerCase() });

    auth.requestOtp(
      { email: email.toLowerCase() },
      (a) => {
        parseLoginError(a?.response?.data?.reason);
      },
      () => onSuccess()
    );
  };

  const parseLoginError = (reason) => {
    switch (reason) {
      case "last_request_within_5_minutes":
        onSuccess();
        break;
      case "record_not_found":
        setMessageModal({
          visible: true,
          title: "Registo não encontrado",
          description: "Não foi possível encontrar o registo associado a este endereço de e-mail.",
        });

        break;

      case "user_blocked":
        setMessageModal({
          visible: true,
          title: "Conta bloqueada",
          description: "A tua conta foi bloqueada. Por favor, contacta o suporte através do e-mail suporte@salespark.io.",
        });
        break;
      case "user_not_active":
        setMessageModal({
          visible: true,
          title: "Utilizador inativo",
          description: "O teu utilizador está inativo. Por favor, contacta o suporte através do e-mail suporte@salespark.io.",
        });
        break;
      default:
        setMessageModal({
          visible: true,
          title: "Erro",
          description:
            "Ocorreu um erro ao tentar fazer login. Por favor, tenta novamente, ou contacta o suporte através do e-mail suporte@salespark.io se o problema persistir.",
        });
        break;
    }
  };

  const parseError = (message) => {
    if (message === "email must be a valid email") {
      return "Endereço de e-mail inválido";
    }
    if (message === "email is a required field") {
      return "Endereço de e-mail é um campo obrigatório";
    }

    return message;
  };

  return (
    <>
      <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <FormControl className="sp-auth__field" fullWidth>
          <label className="sp-auth__label" htmlFor="otp-request-email">
            Endereço de e-mail
          </label>
          <Controller
            name="email"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange, onBlur } }) => (
              <OutlinedInput
                autoFocus
                fullWidth
                id="otp-request-email"
                value={value}
                onBlur={onBlur}
                onChange={(e) => {
                  onChange(e.target.value.replace(/\s/g, ""));
                }}
                error={Boolean(errors.email)}
                placeholder="o-teu-email@exemplo.com"
              />
            )}
          />
          {errors.email && <FormHelperText>{parseError(errors?.email?.message)}</FormHelperText>}
        </FormControl>

        <Button className="sp-auth__submit" type="submit" variant="contained">
          Enviar código
        </Button>
      </form>

      <Link className="sp-auth__back" component={RouterLink} to={pathForAuthRoute("/login", location.search)}>
        <ChevronLeft />
        <span>Voltar ao login</span>
      </Link>

      <ModalMessage
        visible={messageModal.visible}
        close={() => setMessageModal({ visible: false })}
        title={messageModal.title}
        description={messageModal.description}
      />
    </>
  );
};

export default RequestOTP;
